import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { language } from 'constant';
import { UnknownError } from 'components/ErrorPage';
import { Breadcrumbs } from 'components/Breadcrumbs';
import { ContactSupport } from 'sections/ContactSupport';
import BackToLink from 'shared-components/BackToLink';
import { RelatedArticles } from 'sections/RelatedArticles';
import { SwaggerArticle } from 'components/SwaggerArticle';
import generateMainStyles from 'styles/MainStyles';
import { useTheme } from '@emotion/react';
import { Box } from '@mui/material';

let isFetching = false;

export function Article() {
    const { category, article } = useParams();
    const [htmlFileString, setHtmlFileString] = useState<string>('');
    const [isYaml, setIsYaml] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const theme = useTheme();
    const styles = useMemo(() => generateMainStyles(theme), [theme]);

    const fetchHtmlOrYaml = async () => {
        if (isFetching) return;
        isFetching = true;
        try {
            setIsLoading(true);

            const htmlRes = await fetch(`/articles/${language}/${category}/${article}.html`);

            if (htmlRes.ok) {
                const html = await htmlRes.text();

                const isInvalid = !html
                    || html.includes('<div id="root"></div>')
                    || html.includes('<script')
                    || html.includes('<!DOCTYPE html>');

                if (!isInvalid) {
                    setHtmlFileString(html);
                    setIsYaml(false);
                    return;
                }
            }

            const yamlRes = await fetch(`/docs/${language}/${category}/${article}.yaml`);

            if (yamlRes.ok) {
                const yamlText = await yamlRes.text();
                const isYamlValid = yamlText.trim().startsWith('openapi:') || yamlText.trim().startsWith('swagger:');

                if (isYamlValid) {
                    setIsYaml(true);
                    return;
                }
            }

            setHtmlFileString('');
        } catch (error) {
            global.console.error('error:', error);
        } finally {
            setIsLoading(false);
            isFetching = false;
        }
    };

    useEffect(() => {
        fetchHtmlOrYaml();
    }, [category, article]);

    const htmlWithCorrectLinks = useMemo(() => {
        const withImagePaths = htmlFileString.replaceAll(/(\.\.\/)+public\/images/g, '/images');

        return withImagePaths.replace(/<a\s+href="([^"]+)"/g, (match, href) => {
            const isExternal = /^(https?:)?\/\//i.test(href);
            const isMedia = /\.(pdf|mp4|webm|mov|m4v|ogv|avi)(\?|#|$)/i.test(href);

            if (isExternal || isMedia) {
                return `<a href="${href}" target="_blank" rel="noopener noreferrer"`;
            }
            return match;
        });
    }, [htmlFileString]);

    if (isLoading) return <p>Loading...</p>;

    if (!htmlFileString && !isYaml) return <UnknownError />;

    return (
        <Box component="article" sx={styles.articleClass}>
            <Breadcrumbs />
            <BackToLink />
            {isYaml ? (
                <SwaggerArticle url={`/docs/${language}/${category}/${article}.yaml`} />
            ) : (
                // eslint-disable-next-line react/no-danger
                <div dangerouslySetInnerHTML={{ __html: htmlWithCorrectLinks }} />
            )}
            <ContactSupport />
            {category && article && <RelatedArticles parentArticleCategory={category} parentArticleTitle={article} />}
        </Box>
    );
}
